import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, HistoryPreview } from '../../api';
import { historyEndpoints } from '../../api';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../../ui';
import { dollars } from '../event/shared';

/**
 * Sales history from the previous system (D-364).
 *
 * The "Primary Sales Lines" export, a file at a time, going back to 2024. None
 * of it becomes an order or a ticket: it is what the customer record, the
 * customer's "Historical orders", audiences and the giving letter read.
 *
 * Same rule as the imports above: the dry run is read first, and the import
 * button does not exist until one comes back clean. Two things only this screen
 * can fix, so it offers them in place:
 *
 *   - a donation-type item it has not seen before has to be named as a gift,
 *     the card surcharge or something else, because the export calls all three
 *     "Donation" and only one of them belongs on a tax letter;
 *   - the files must not overlap, so the list of what is already in says which
 *     days are taken.
 */

const readFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });

const KIND_LABEL = { GIFT: 'A gift', SURCHARGE: 'Card surcharge', OTHER: 'Something else' } as const;

export function HistoryImportPanel() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [preview, setPreview] = useState<HistoryPreview | null>(null);
  const [done, setDone] = useState<HistoryPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const files = useQuery({ queryKey: ['history', 'files'], queryFn: () => historyEndpoints.files() });

  const body = () => ({ file_name: file?.name ?? '', csv: file?.text ?? '' });
  const runPreview = useMutation({
    mutationFn: () => historyEndpoints.preview(body()),
    onSuccess: (data) => {
      setPreview(data);
      setDone(null);
      setError(null);
    },
    onError: (cause: ApiError) => {
      setPreview(null);
      setError(cause.message);
    },
  });
  const runImport = useMutation({
    mutationFn: () => historyEndpoints.import(body()),
    onSuccess: (data) => {
      setDone(data.preview);
      setPreview(null);
      setFile(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['history', 'files'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const classify = useMutation({
    mutationFn: (input: { item_name: string; line_kind: 'GIFT' | 'SURCHARGE' | 'OTHER' }) => historyEndpoints.classify(input),
    // Classifying changes what the file means, so the dry run is run again rather than patched.
    onSuccess: () => runPreview.mutate(),
    onError: (cause: ApiError) => setError(cause.message),
  });
  const busy = runPreview.isPending || runImport.isPending || classify.isPending;

  return (
    <>
      <Panel
        title="Import sales history from the previous system"
        description="The Primary Sales Lines export, one file at a time. It becomes history on the customer record, the customer's Historical orders, customer segments and the giving letter (2026 on). It never becomes an order or a ticket, and nothing goes to QuickBooks. Files must not overlap: export each range to start the day after the last one ended."
      >
        <div className="space-y-4 px-5 py-4">
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Sales Data Extract (Primary Sales Lines)</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={async (e) => {
                setPreview(null);
                setDone(null);
                const chosen = e.target.files?.[0];
                if (!chosen) return setFile(null);
                try {
                  setFile({ name: chosen.name, text: await readFile(chosen) });
                } catch (cause) {
                  setError((cause as Error).message);
                }
              }}
              className="mt-1 block text-sm"
            />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" disabled={!file || busy} onClick={() => runPreview.mutate()}>
              {runPreview.isPending ? 'Reading…' : 'Dry run'}
            </Button>
            {preview?.ready ? (
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`Import ${preview.lines} lines (${preview.orders} orders, ${preview.first_sale_date} to ${preview.last_sale_date})? Those days cannot be imported again.`)) runImport.mutate();
                }}
              >
                {runImport.isPending ? 'Importing…' : `Import ${preview.orders} orders`}
              </Button>
            ) : null}
            <p className="text-xs text-ink-400">The dry run writes nothing.</p>
          </div>
        </div>

        {preview && preview.unclassified.length > 0 ? (
          <div className="border-t border-line px-5 py-4">
            <p className="text-sm text-ink-900">
              The old system files these as donations. Say what each one really is; it cannot be changed afterward, and only a gift goes on a tax letter.
            </p>
            <ul className="mt-3 space-y-2">
              {preview.unclassified.map((item) => (
                <li key={item.item_name} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-ink-900">“{item.item_name}”</span>
                  <span className="text-ink-400">
                    {item.lines} line{item.lines === 1 ? '' : 's'}, first on line {item.first_line}
                  </span>
                  {(['GIFT', 'SURCHARGE', 'OTHER'] as const).map((kind) => (
                    <Button
                      key={kind}
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`Treat every “${item.item_name}” line as ${KIND_LABEL[kind].toLowerCase()}? This cannot be changed afterward.`)) classify.mutate({ item_name: item.item_name, line_kind: kind });
                      }}
                    >
                      {KIND_LABEL[kind]}
                    </Button>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Panel>

      {preview && preview.problems.length > 0 ? (
        <Panel title={`${preview.problems.length} problem${preview.problems.length === 1 ? '' : 's'}: nothing will be imported`} description="A file is imported whole or not at all.">
          <Table headers={['Line', 'What is wrong']}>
            {preview.problems.map((problem, index) => (
              <tr key={`${problem.code}-${index}`} className="border-b border-line last:border-0">
                <td className="px-5 py-2 text-sm text-ink-400">{problem.line ?? '—'}</td>
                <td className="px-5 py-2 text-sm text-ink-900">
                  <Badge tone="bad">{problem.code.toLowerCase().replaceAll('_', ' ')}</Badge> {problem.message}
                </td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      {preview && preview.problems.length === 0 ? <HistorySummary preview={preview} title={`${preview.orders} orders from ${preview.first_sale_date} to ${preview.last_sale_date}`} /> : null}
      {done ? <HistorySummary preview={done} title={`Imported ${done.orders} orders from ${done.first_sale_date} to ${done.last_sale_date}`} /> : null}

      <Panel title="Sales history imported" description="In date order. A gap between one file's last day and the next file's first is days nobody has imported yet.">
        <Table headers={['File', 'Sale dates', 'Lines', 'Orders', 'Net', 'Imported']}>
          {(files.data?.files ?? []).length === 0 ? (
            <EmptyRow columns={6}>{files.data ? 'None yet.' : 'Loading…'}</EmptyRow>
          ) : (
            files.data!.files.map((row) => (
              <tr key={row.id} className="border-b border-line last:border-0">
                <td className="px-5 py-2 text-sm text-ink-900">{row.file_name}</td>
                <td className="px-5 py-2 text-sm whitespace-nowrap text-ink-600">
                  {row.first_sale_date} to {row.last_sale_date}
                </td>
                <td className="tabular px-5 py-2 text-sm">{row.line_count}</td>
                <td className="tabular px-5 py-2 text-sm">{row.order_count}</td>
                <td className="tabular px-5 py-2 text-sm">{dollars(row.net_cents)}</td>
                <td className="px-5 py-2 text-sm whitespace-nowrap text-ink-600">
                  {formatWhen(row.imported_at)}
                  {row.imported_by ? ` · ${row.imported_by}` : ''}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </>
  );
}

/** What a file comes to: check these against the old system's own totals for the same days. */
function HistorySummary({ preview, title }: { preview: HistoryPreview; title: string }) {
  return (
    <Panel
      title={title}
      description={`${preview.lines} lines, ${preview.people} people with an email, ${preview.tickets} tickets net of refunds. Tickets ${dollars(preview.totals.TICKET)} · gifts ${dollars(preview.totals.GIFT)} · card surcharge ${dollars(preview.totals.SURCHARGE)} · other ${dollars(preview.totals.OTHER)} · net ${dollars(preview.totals.net)}.`}
    >
      <Table headers={['Show', 'Started', 'Tickets', '']}>
        {preview.shows.length === 0 ? (
          <EmptyRow columns={4}>No shows in this file.</EmptyRow>
        ) : (
          preview.shows.map((show) => (
            <tr key={`${show.starts_at}-${show.name}`} className="border-b border-line last:border-0">
              <td className="px-5 py-2 text-sm text-ink-900">{show.name}</td>
              <td className="px-5 py-2 text-sm whitespace-nowrap text-ink-600">{formatWhen(show.starts_at)}</td>
              <td className="tabular px-5 py-2 text-sm">{show.tickets}</td>
              <td className="px-5 py-2 text-sm">
                {show.moved_over ? <Badge tone="warn">moved over; its tickets are already real orders here and are left out</Badge> : null}
              </td>
            </tr>
          ))
        )}
      </Table>
    </Panel>
  );
}
